import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera,
  faCheck,
  faXmark,
  faArrowRight,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faUserGraduate,
  faChalkboardUser,
  faCropSimple,
} from '@fortawesome/free-solid-svg-icons';
import ImageCropper from '../components/ImageCropper/ImageCropper';
import './PhotoApprovals.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const when = (v) => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const Pic = ({ src, role, label }) => {
  const [broken, setBroken] = useState(false);
  return (
    <figure className="rmaph-pic">
      <span>{src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : <FontAwesomeIcon icon={role === 'teacher' ? faChalkboardUser : faUserGraduate} />}</span>
      <figcaption>{label}</figcaption>
    </figure>
  );
};

/* /Admin/photos: new profile photos sent by students and teachers, waiting for the admin's OK */
const PhotoApprovals = () => {
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const [fixing, setFixing] = useState(null); // the photo being turned / cropped ({ ...photo, src: blob url })

  // load the waiting photo through the API (the crop window can't read /assets files directly)
  const openFix = async (p) => {
    const key = `${p.role}-${p.id}`;
    setBusy(key);
    try {
      const res = await axios.get(`${API}/api/admin/photos/${p.role}/${p.id}/file`, { ...auth(), responseType: 'blob' });
      setFixing({ ...p, src: URL.createObjectURL(res.data) });
    } catch {
      setToast({ msg: 'Couldn’t open this photo.', bad: true });
    } finally {
      setBusy('');
    }
  };
  const closeFix = () => {
    if (fixing?.src) URL.revokeObjectURL(fixing.src);
    setFixing(null);
  };

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/photos`, auth());
      setList(res.data?.photos || []);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the photos.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const act = async (p, action) => {
    if (action === 'reject' && !window.confirm(`Reject the new photo of ${p.name}? They will be asked to send a real photo of themselves.`)) return;
    const key = `${p.role}-${p.id}`;
    setBusy(key);
    try {
      await axios.post(`${API}/api/admin/photos/${p.role}/${p.id}/${action}`, {}, auth());
      setList((l) => l.filter((x) => `${x.role}-${x.id}` !== key));
      setToast({ msg: action === 'approve' ? `${p.name}: new photo approved.` : `${p.name}: photo rejected.` });
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Something went wrong.', bad: true });
      load();
    } finally {
      setBusy('');
    }
  };

  // turned / cropped by the admin: replaces the waiting photo (still to approve)
  const saveFixed = async (file) => {
    const p = fixing;
    closeFix();
    if (!p) return;
    const key = `${p.role}-${p.id}`;
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await axios.post(`${API}/api/admin/photos/${p.role}/${p.id}/replace`, fd, auth());
      setList((l) => l.map((x) => (`${x.role}-${x.id}` === key ? { ...x, pending: res.data?.pending || x.pending } : x)));
      setToast({ msg: `${p.name}: photo fixed. Check it and approve.` });
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Couldn’t save the photo.', bad: true });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="rmaph">
      <header className="rmaph-head">
        <h1>
          <FontAwesomeIcon icon={faCamera} /> Photo approvals
        </h1>
        <p>Students and teachers can change their profile photo. Their old photo stays until you approve the new one. Only approve a real photo of the person.</p>
      </header>

      {error && !list ? (
        <div className="rmaph-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <p>{error}</p>
          <button type="button" className="rmaph-btn" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      ) : !list ? (
        <div className="rmaph-skel" aria-busy="true" />
      ) : list.length === 0 ? (
        <div className="rmaph-empty">
          <FontAwesomeIcon icon={faCircleCheck} />
          <p>No photos waiting. All done!</p>
        </div>
      ) : (
        <ul className="rmaph-list">
          {list.map((p) => {
            const key = `${p.role}-${p.id}`;
            return (
              <li key={key} className="rmaph-card">
                <div className="rmaph-who">
                  <a href={`/Admin/${p.role === 'teacher' ? 'teacherprofile' : 'studentprofile'}/${p.id}`} target="_blank" rel="noopener noreferrer">
                    {p.role === 'teacher' ? 'Teacher ' : ''}
                    {p.name}
                  </a>
                  <span className={`rmaph-role is-${p.role}`}>{p.role === 'teacher' ? 'Teacher' : 'Student'}</span>
                  <small>{when(p.at)}</small>
                </div>
                <div className="rmaph-compare">
                  <Pic src={p.current} role={p.role} label="Now" />
                  <FontAwesomeIcon icon={faArrowRight} className="rmaph-arrow" />
                  <Pic src={p.pending} role={p.role} label="New" />
                </div>
                <div className="rmaph-actions">
                  <button type="button" className="rmaph-btn rmaph-btn--ghost" disabled={busy === key} onClick={() => openFix(p)} title="Turn a sideways photo or crop it">
                    <FontAwesomeIcon icon={faCropSimple} /> Rotate / crop
                  </button>
                  <button type="button" className="rmaph-btn rmaph-btn--ghost" disabled={busy === key} onClick={() => act(p, 'reject')}>
                    <FontAwesomeIcon icon={faXmark} /> Reject
                  </button>
                  <button type="button" className="rmaph-btn" disabled={busy === key} onClick={() => act(p, 'approve')}>
                    <FontAwesomeIcon icon={faCheck} /> Approve
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {fixing && (
        <ImageCropper
          src={fixing.src}
          title={`Fix the photo of ${fixing.name}`}
          onCancel={closeFix}
          onDone={saveFixed}
        />
      )}

      {toast && (
        <div className={`rmaph-toast ${toast.bad ? 'is-bad' : ''}`} role="status">
          <FontAwesomeIcon icon={toast.bad ? faTriangleExclamation : faCircleCheck} /> {toast.msg}
        </div>
      )}
    </div>
  );
};

export default PhotoApprovals;
